import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { EmbedSettingsSchema } from "../../../../schemas/Deployment/deploymentSettings/Embed/EmbedSettings.schema";
import { optionalQueryParam, PaginationParamsSchema } from "./common.schemas";

/**
 * Deployment channel. Derived from the generated `DeploymentType` values so the
 * contract cannot offer a channel the database has no column value for.
 *
 * Deriving rather than restating is safe in BOTH directions here, and the
 * guarantee is structural rather than a convention:
 * `DEPLOYMENT_SETTINGS_SCHEMAS` in
 * `packages/types/src/schemas/Deployment/deploymentSettings/__json.schema.ts`
 * is declared `satisfies Record<DbEnum.DeploymentType, z.ZodTypeAny>`, so a new
 * Prisma member is a COMPILE error until it has a settings schema. A value that
 * reaches this enum therefore already has somewhere for its settings to land.
 *
 * Restating the list by hand is what produced the two defects this replaces:
 * `SMS` was offered and could not be stored (no Prisma member, and
 * `validateDeploymentSettings("SMS", …)` throws a TypeError on the undefined
 * schema, so the create answered 500 rather than a validation error), and
 * `INSTAGRAM` shipped with receiver, sender, validator and settings schema
 * while being invisible to every API consumer. `TWILIO_SMS` is the SMS channel.
 */
export const DeploymentTypeSchema = z.enum(DbEnum.DEPLOYMENT_TYPE_VALUES);

export const DeploymentSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  type: DeploymentTypeSchema,
  agentId: z.string().uuid().nullable(),
  agentName: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable()
});

/**
 * Inbound push-subscription state for OAuth email channels (Gmail Pub/Sub
 * watch / Outlook Graph subscription). Distinct from connectionStatus, which
 * tracks OAuth token health only — inbound email arrives only while ACTIVE.
 */
export const InboundWebhookStateSchema = z.object({
  status: z.enum(["ACTIVE", "EXPIRED", "NOT_CONFIGURED"]),
  expiresAt: z.coerce.date().nullable()
});

export const DeploymentDetailSchema = DeploymentSummarySchema.extend({
  settings: z.unknown().nullable(),
  connectionStatus: z.string().nullable(),
  // nullish (not nullable): tolerate older backends that omit the field
  inboundWebhook: InboundWebhookStateSchema.nullish()
});

/** Response data for `GET /public/v1/deployments`. */
export const ListDeploymentsResponseSchema = z.array(DeploymentSummarySchema);
export type ListDeploymentsResponse = z.infer<typeof ListDeploymentsResponseSchema>;

export const ListDeploymentsParamsSchema = PaginationParamsSchema.extend({
  search: z.string().optional(),
  type: DeploymentTypeSchema.optional(),
  isActive: z.preprocess(
    (v) => (v === "true" ? true : v === "false" ? false : v),
    z.boolean().optional()
  ),
  /**
   * Only the deployments serving this agent. Narrows WITHIN the caller's own
   * organization — another organization's agent id matches nothing.
   *
   * 🔴 THIS KEY WAS MISSING AND NOTHING SAID SO (NEX-5806). The object is not
   * `.strict()`, so an undeclared `?agentId=` was stripped at parse and the whole
   * organization's list came back, answered 200. A caller taking `data[0]` got
   * another agent's deployment. `.strict()` is deliberately NOT the fix: it would
   * 400 every client already sending an extra param this route ignores.
   *
   * ⚠️ An EMPTY `?agentId=` means "no filter", as it does for every optional
   * query id on this surface (`optionalQueryParam`, pinned by
   * `id-validation-consistency.test.ts`). The CLI refuses an empty
   * `--agent-id` before it gets here, because `--agent-id "$UNSET"` would
   * otherwise ask for every agent while meaning one.
   */
  agentId: optionalQueryParam(z.string().uuid())
});

/**
 * JSON-safe value type: permits only valid JSON primitives, arrays, and objects.
 * Rejects functions, symbols, undefined, and other non-JSON types.
 */
const JsonValue: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValue),
    z.record(z.string(), JsonValue)
  ])
);

const MAX_SETTINGS_KEYS = 50;
const MAX_SETTINGS_BYTES = 50 * 1024; // 50 KB

/**
 * Bounded settings schema: accepts a JSON-safe record with limits on
 * the number of top-level keys and the total serialized size.
 */
const BoundedSettingsSchema = z
  .record(z.string(), JsonValue)
  .refine((obj) => Object.keys(obj).length <= MAX_SETTINGS_KEYS, {
    message: `Settings must have at most ${MAX_SETTINGS_KEYS} top-level keys`
  })
  .refine(
    (obj) => {
      try {
        return JSON.stringify(obj).length <= MAX_SETTINGS_BYTES;
      } catch {
        return false;
      }
    },
    { message: `Settings must be at most ${MAX_SETTINGS_BYTES / 1024}KB when serialized` }
  );

export const CreateDeploymentBodySchema = z.object({
  name: z.string().trim().min(1),
  type: DeploymentTypeSchema,
  agentId: z.string().uuid().optional(),
  description: z.string().optional(),
  settings: BoundedSettingsSchema.optional(),
  phoneNumberId: z.string().uuid().optional(),
  oauthConnectionId: z.string().uuid().optional(),
  apiKeyConnectionId: z.string().uuid().optional(),
  whatsappSenderId: z.string().optional()
});

export const UpdateDeploymentBodySchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().nullable().optional(),
  agentId: z.string().uuid().nullable().optional(),
  settings: BoundedSettingsSchema.optional(),
  isActive: z.boolean().optional(),
  phoneNumberId: z.string().uuid().nullable().optional(),
  oauthConnectionId: z.string().uuid().nullable().optional(),
  apiKeyConnectionId: z.string().uuid().nullable().optional()
});

export const DeploymentIdParamSchema = z.object({
  deploymentId: z.string().uuid()
});

/**
 * The subprotocol a voice client offers beside its token, and the one the relay
 * selects on a successful upgrade. The browser `WebSocket` API can set no other
 * header, so the single-use token rides in `Sec-WebSocket-Protocol`, never in
 * the URL, where every proxy and access log would record it:
 *
 *     new WebSocket(url, [VOICE_SESSION_SUBPROTOCOL, VOICE_SESSION_TOKEN_SUBPROTOCOL_PREFIX + token])
 *
 * The marker is what the relay echoes back, so the token is never in the
 * handshake response either.
 */
export const VOICE_SESSION_SUBPROTOCOL = "nexus.voice-relay.v1";

/**
 * Prefix of the subprotocol entry carrying the token. The token is
 * `<base64url>.<base64url>`, every character an RFC 7230 `tchar`, so it needs
 * no further encoding.
 */
export const VOICE_SESSION_TOKEN_SUBPROTOCOL_PREFIX = "nexus.voice-token.";

/**
 * Response of `POST /public/v1/deployments/:deploymentId/voice-session`.
 * The short-lived, single-use credential a custom app uses to open the
 * streaming voice WebSocket: `websocketPath` on the same API host, with the
 * token offered as the subprotocol `VOICE_SESSION_TOKEN_SUBPROTOCOL_PREFIX + token`
 * beside `VOICE_SESSION_SUBPROTOCOL`. A `?token=` query is still accepted and
 * is deprecated — a URL is logged on the way in.
 */
export const CreateVoiceSessionResponseSchema = z.object({
  token: z.string(),
  /** Synthetic session id (the voice relay's callSid). */
  sessionId: z.string(),
  expiresInSeconds: z.number(),
  /** WebSocket path to open against the API host, e.g. "/voice-relay-web". */
  websocketPath: z.string()
});

/**
 * Body of `POST /public/v1/deployments/:deploymentId/chat-session`.
 *
 * `.strict()` so an unknown key is refused BY NAME rather than stripped. A
 * silently discarded `identityHash` would mint an anonymous session while the
 * caller believes it supplied a verified identity — a 201 that reports success
 * for a request that did something else.
 *
 * Every field is optional: the minimal call names only the deployment in the
 * path and mints an anonymous session on a fresh conversation.
 */
export const CreateChatSessionBodySchema = z
  .object({
    /**
     * The customer's own id for this visitor. Carried into the session token
     * only when identity verification is satisfied — otherwise the session is
     * anonymous.
     */
    externalUserId: z.string().trim().min(1).max(255).optional(),
    /**
     * HMAC-SHA256 hex of `externalUserId` under the deployment's embed
     * `identityVerificationSecret`. Required — and only meaningful — when the
     * deployment has `identityVerificationEnabled`.
     */
    identityHash: z.string().min(1).optional(),
    /**
     * Resume an existing conversation. It must belong to this organization AND
     * this deployment; anything else is a 404, the same answer an id that does
     * not exist gets.
     *
     * Omit it to start a new conversation: the response carries the reserved
     * `chatId`, and the row is created on the first message.
     */
    chatId: z.string().uuid().optional()
  })
  .strict();

export type CreateChatSessionBody = z.infer<typeof CreateChatSessionBodySchema>;

/**
 * Body of `POST /public/v1/deployments/:deploymentId/chat-session/anonymous`.
 *
 * ## Every field is optional, and that is the compatibility contract
 *
 * 🔴 **A BODY-LESS POST MUST KEEP WORKING EXACTLY AS IT DID.** The route shipped
 * with no body at all and the embed widget still sends none, so this schema
 * accepts `{}` — and the handler parses
 * {@link CreateAnonymousChatSessionOrAbsentBodySchema}, which is this schema
 * made total over the `undefined` an absent body really produces. Both halves
 * are what make this a non-breaking v1 addition rather than a new route.
 *
 * ## Sent form-encoded by a browser, and JSON is accepted too
 *
 * `application/x-www-form-urlencoded` is a CORS-SAFELISTED content type, so a
 * POST carrying one stays a CORS *simple* request and costs no preflight. The
 * widget's own mint calls that property load-bearing (`session.ts`), and the
 * customers who send this body are exactly the ones whose first token matters
 * most. The backend registers both `express.json` and `express.urlencoded`
 * globally, so a server-side caller sending JSON is parsed identically; the
 * choice is the CALLER's and neither shape reaches a different validator.
 *
 * `.strict()` for the same reason its org-key sibling gives: a silently
 * stripped `identityHash` would mint an anonymous session while the caller
 * believes it claimed a verified identity — a 201 reporting success for a
 * request that did something else.
 */
export const CreateAnonymousChatSessionBodySchema = z
  .object({
    /**
     * The customer's own id for this visitor, exactly as their server signed
     * it. Carried into the session token only when the accompanying
     * `identityHash` verifies; anything else is a `403`.
     */
    externalUserId: z.string().trim().min(1).max(255).optional(),
    /**
     * HMAC-SHA256 hex of `externalUserId` under the deployment's embed
     * `identityVerificationSecret` — the same `data-user-hash` the console
     * widget already reads off its script tag.
     *
     * Bounded at 128 characters where the org-key sibling is unbounded, because
     * this route takes no credential: an unauthenticated caller's string should
     * not be arbitrarily long, and a correct value is 64. Over the bound is a
     * `400` — a statement about the string the caller typed, identical for a
     * deployment that exists and one that does not, exactly like the `400` a
     * non-UUID `deploymentId` already gets.
     */
    identityHash: z.string().trim().min(1).max(128).optional(),
    /**
     * The host ticket the Nexus widget (`widget.js`) handed this iframe in its
     * URL fragment. Present means the first-party widget, on the page whose
     * origin the ticket attests: the mint then admits the deployment under the
     * console widget's own consent rule (EMBED, public, page on the allowlist)
     * rather than requiring `allowAnonymousChatSessions`. It is an attestation of
     * where the widget was served, not a credential: everything the mint decides
     * is still read off the deployment. A ticket that does not verify is the same
     * `404` every other refusal is.
     */
    hostTicket: z.string().min(1).max(1024).optional(),
    /**
     * The host page's continuity key — the `sid` the widget keeps in the page's
     * own `localStorage` — so a visitor who comes back resumes their
     * conversation. Only with a `hostTicket`: a bare key from a caller holding
     * nothing would be a bearer key over a transcript with no door in front of it.
     */
    serviceThreadId: z.string().uuid().optional()
  })
  .strict()
  .refine((body) => body.serviceThreadId === undefined || body.hostTicket !== undefined, {
    message: "serviceThreadId requires hostTicket",
    path: ["serviceThreadId"]
  });

export type CreateAnonymousChatSessionBody = z.infer<typeof CreateAnonymousChatSessionBodySchema>;

/**
 * The same schema, TOTAL over the absent body a body-less POST really produces.
 *
 * 🔴 **`@Body()` HANDS THE HANDLER `undefined`, NOT `{}`, AND THAT IS MEASURED
 * RATHER THAN ASSUMED.** `body-parser` 1.x opens with
 * `req.body = req.body || {}` before it decides whether the `Content-Type`
 * matches; **2.x does not**, and `@nestjs/platform-express@11` depends on
 * `express@5`, whose parsers are 2.x. So a POST with no body and no
 * `Content-Type` — the request the embed widget sends, and the one that keeps
 * it a CORS *simple* request — reaches a `.strict()` object parse as
 * `undefined` and is a **400**.
 *
 * ⚠️ **AND THE TWO PARSERS COEXIST IN THIS REPO, WHICH IS WHY THE PLAIN SCHEMA
 * LOOKED SUFFICIENT.** `apps/backend/src/main.ts` registers `express.json()`
 * from its OWN `express` dependency (4.x, i.e. body-parser 1.x, which does
 * assign `{}`), while the adapter Nest runs the app on is express 5. Whichever
 * of the two answers first decides the case, and a route that is correct under
 * only one of them is a route whose behaviour depends on a hoisting outcome.
 * `apps/backend/src/public/v1/__tests__/anonymous-mint-accepts-no-body.spec.ts`
 * pins the STRICTER parser, so this schema makes the route correct under both.
 *
 * ## Why a second exported symbol rather than one total schema
 *
 * The descriptor's `Body` must stay a plain `ZodObject`: `Body?.shape` is read
 * by `__governance__/v1-write-door-census.ts` and by the admin schema index,
 * and a `ZodPipe` answers `undefined` there — a silent narrowing rather than a
 * failure. So the contract keeps the object and the handler parses this, and
 * the two cannot drift because this one is DERIVED from that one in the
 * statement below.
 *
 * ⚠️ **THE NAME ENDS IN `BodySchema` BECAUSE THE SUFFIX IS A CONTRACT, NOT A
 * LABEL.** `v1-schema-parse-source-convention.spec.ts` reads a v1 schema's
 * export-name suffix as its PARSE SOURCE, and `@nexus/types`'s own
 * `id-validation-consistency.test.ts` infers the same thing from the same
 * letters — because that package cannot import a controller to check. A name
 * ending `…OrAbsentSchema` is classified by neither, and the gate reports it as
 * *unclassified* rather than passing it, which is how this one was found.
 *
 * ⚠️ It also cannot be written as a `?? {}` at the call site:
 * `nexus/require-body-validation` matches `.parse(<the parameter name>)` as
 * TEXT, so any expression in that argument position reads to it as an
 * unvalidated body and reds at `error`.
 */
export const CreateAnonymousChatSessionOrAbsentBodySchema = z.preprocess(
  (value) => value ?? {},
  CreateAnonymousChatSessionBodySchema
);

/**
 * Response of BOTH chat-session mints — `.../chat-session` and
 * `.../chat-session/anonymous`.
 *
 * The short-lived credential a customer's BROWSER holds. One schema for two
 * doors deliberately: the credential is the same credential, verified by the
 * same guard, and a second declaration of the same four fields is a second
 * thing to drift.
 *
 * ## The two doors differ in WHO ASKS, never in what comes back
 *
 * - `.../chat-session` — the customer's SERVER calls it with an org API key
 *   carrying `chat_sessions:execute`. The key never reaches a browser; that is
 *   the whole point of the two-hop model, and it is the right door for anyone
 *   who has a backend.
 * - `.../chat-session/anonymous` — the BROWSER calls it directly, holding
 *   nothing. It exists because a first-party embed served from a static host
 *   has no server of ours or theirs in the path, so there is nobody to hold a
 *   key. It is refused unless the deployment itself opted in
 *   (`securitySettings.allowAnonymousChatSessions`) or the call carries the
 *   Nexus widget's `hostTicket`, and it is narrower on exactly ONE axis: it
 *   cannot resume a conversation a caller NAMES — only, beside a verified
 *   ticket, the one the host page's own thread key holds. It CAN carry an
 *   identity — `externalUserId` plus the `identityHash` the deployment's embed
 *   secret signs — which is the whole reason a static-host embed can attribute
 *   a conversation to a real user without an org key in the page.
 *
 * ## The token expires, and a 401 is the signal to come back here
 *
 * `expiresInSeconds` is the bearer lifetime. A client should treat ANY 401 from
 * a chat route as "this credential is finished" and ask its own server for a
 * fresh session — the same call that started this one. There is no way to tell
 * the causes apart from outside, deliberately: expired, revoked, wrong
 * deployment and forged all answer identically, so a caller cannot use the
 * refusal to learn which deployment or conversation ids are real.
 */
export const CreateChatSessionResponseSchema = z.object({
  /** The bearer credential. Opaque — do not parse it. */
  token: z.string(),
  /** This session's own id. Useful for correlating server-side records with a visitor. */
  sessionId: z.string(),
  /**
   * The conversation this token addresses — the one that was resumed, or the
   * one reserved for the first message.
   */
  chatId: z.string(),
  /** Bearer lifetime in seconds. */
  expiresInSeconds: z.number()
});

/**
 * Response of `.../chat-session/anonymous` — the shared session shape, plus the
 * one fact only this mint can report.
 *
 * Its own schema rather than an optional field on the shared one: the org-key
 * mint and the refresh never resume by thread, so a field they can never set
 * would be a promise in their published shape that nothing keeps.
 */
export const CreateAnonymousChatSessionResponseSchema = CreateChatSessionResponseSchema.extend({
  /**
   * Present only when the call carried a `serviceThreadId` beside a verified
   * `hostTicket`: `true` when the mint resumed the conversation that thread
   * already holds, `false` when it reserved a fresh one under that key.
   */
  resumed: z.boolean().optional()
});

export const DeploymentStatsSchema = z.object({
  totalSessions: z.number(),
  totalMessages: z.number(),
  sessions: z.array(
    z.object({
      id: z.string(),
      chatId: z.string().nullable(),
      messageCount: z.number(),
      updatedAt: z.coerce.date(),
      createdAt: z.coerce.date()
    })
  )
});

/**
 * The embed widget's appearance settings — the SAME object the widget renders
 * from, minus the one field it must never publish.
 *
 * A widget stores its appearance at `deploymentSettings.embedSettings`, whose
 * schema is `EmbedSettingsSchema`. Deriving from that schema rather than
 * restating a friendlier subset is the whole point: this surface previously
 * declared eight flat names (`theme`, `primaryColor`, `position`,
 * `initialMessage`, `suggestedMessages`, `logoUrl`, `avatarUrl`,
 * `headerTitle`) of which `EmbedSettingsSchema` declares exactly one, so every
 * read answered `null` and every PATCH wrote keys no settings schema declares
 * into the column. Two of the eight (`logoUrl`, `avatarUrl`) have no widget
 * counterpart at all, so no rename could have rescued the shape — a hand-kept
 * translation layer was the defect, not the spelling.
 *
 * 🔴 `identityVerificationSecret` is OMITTED, and that is a security boundary
 * rather than tidiness. It is the server-side HMAC key that authenticates a
 * visitor's `externalUserId`; publishing it to any holder of a
 * `deployments:read` API key would let that holder forge an identity. It stays
 * writable and readable only where it already was — the dashboard — and the
 * v1 PATCH preserves whatever is stored rather than dropping what it cannot
 * see.
 */
export const EmbedConfigSchema = EmbedSettingsSchema.omit({
  identityVerificationSecret: true
});

export type EmbedConfig = z.infer<typeof EmbedConfigSchema>;

/**
 * PATCH body — every readable key, all optional. Symmetric with
 * `EmbedConfigSchema` by construction, so a caller can round-trip a read
 * through a write without a field being silently unwritable.
 */
export const UpdateEmbedConfigBodySchema = EmbedConfigSchema.partial();
